import { type SEOHandle } from "@nasa-gcn/remix-seo";
import {
  type HeadersFunction,
  json,
  type LoaderFunctionArgs,
  type MetaFunction,
} from "@remix-run/node";
import {
  Link,
  Outlet,
  useFetcher,
  useLoaderData,
  useParams,
  useSearchParams,
} from "@remix-run/react";
import { Locate } from "lucide-react";
import React, {
  useCallback,
  useEffect,
  useState,
  useMemo,
  useRef,
} from "react";
import { ClientOnly } from "remix-utils/client-only";
import { serverOnly$ } from "vite-env-only/macros";

import CarPreview from "#app/components/car-preview.tsx";
import CitySuggestions from "#app/components/city-suggestions.tsx";
import { GeneralErrorBoundary } from "#app/components/error-boundary.tsx";
import { Combobox } from "#app/components/forms.tsx";
import Mapa from "#app/components/mapa.jsx";
import Station from "#app/components/station.tsx";
import { Button } from "#app/components/ui/button.tsx";
import { Skeleton } from "#app/components/ui/skeleton";
import { prisma } from "#app/utils/db.server.ts";
import { getRegionalStations } from "#app/utils/distance.server.ts";
import { formatState } from "#app/utils/format-text.tsx";
import { reuseUsefulLoaderHeaders } from "#app/utils/misc.tsx";
import {
  // combineServerTimings,
  makeTimings,
  time,
} from "#app/utils/timing.server.ts";
import { useOptionalUser } from "#app/utils/user.ts";

interface StateCoordinates {
  [key: string]: {
    center: {
      lat: number;
      lng: number;
    };
    zoom: number;
  };
}

// Define the state coordinates map
const STATE_COORDINATES: StateCoordinates = {
  "acre (ac)": {
    center: { lat: -8.77, lng: -70.55 },
    zoom: 8,
  },
  "alagoas (al)": {
    center: { lat: -9.71, lng: -35.73 },
    zoom: 9,
  },
  "amazonas (am)": {
    center: { lat: -3.07, lng: -61.66 },
    zoom: 7,
  },
  "amapá (ap)": {
    center: { lat: 1.41, lng: -51.77 },
    zoom: 8,
  },
  "bahia (ba)": {
    center: { lat: -12.96, lng: -38.51 },
    zoom: 8,
  },
  "ceará (ce)": {
    center: { lat: -3.71, lng: -38.54 },
    zoom: 8,
  },
  "distrito federal (df)": {
    center: { lat: -15.83, lng: -47.86 },
    zoom: 9,
  },
  "espírito santo (es)": {
    center: { lat: -19.19, lng: -40.34 },
    zoom: 8,
  },
  "goiás (go)": {
    center: { lat: -16.64, lng: -49.31 },
    zoom: 9,
  },
  "maranhão (ma)": {
    center: { lat: -2.55, lng: -44.3 },
    zoom: 8,
  },
  "mato grosso (mt)": {
    center: { lat: -12.64, lng: -55.42 },
    zoom: 8,
  },
  "mato grosso do sul (ms)": {
    center: { lat: -20.51, lng: -54.54 },
    zoom: 8,
  },
  "minas gerais (mg)": {
    center: { lat: -18.1, lng: -44.38 },
    zoom: 8,
  },
  "pará (pa)": {
    center: { lat: -5.53, lng: -52.29 },
    zoom: 8,
  },
  "paraíba (pb)": {
    center: { lat: -7.06, lng: -35.55 },
    zoom: 8,
  },
  "paraná (pr)": {
    center: { lat: -24.89, lng: -51.55 },
    zoom: 8,
  },
  "pernambuco (pe)": {
    center: { lat: -8.28, lng: -35.07 },
    zoom: 8,
  },
  "piauí (pi)": {
    center: { lat: -8.28, lng: -43.68 },
    zoom: 8,
  },
  "rio de janeiro (rj)": {
    center: { lat: -22.84, lng: -43.15 },
    zoom: 8,
  },
  "rio grande do norte (rn)": {
    center: { lat: -5.22, lng: -36.52 },
    zoom: 8,
  },
  "rio grande do sul (rs)": {
    center: { lat: -30.01, lng: -51.22 },
    zoom: 8,
  },
  "rondônia (ro)": {
    center: { lat: -11.22, lng: -62.8 },
    zoom: 8,
  },
  "roraima (rr)": {
    center: { lat: 1.89, lng: -61.22 },
    zoom: 8,
  },
  "santa catarina (sc)": {
    center: { lat: -27.33, lng: -49.44 },
    zoom: 8,
  },
  "sergipe (se)": {
    center: { lat: -10.9, lng: -37.07 },
    zoom: 8,
  },
  "são paulo (sp)": {
    center: { lat: -23.55, lng: -46.64 },
    zoom: 8,
  },
  "tocantins (to)": {
    center: { lat: -10.25, lng: -48.25 },
    zoom: 8,
  },
};

// Helper function to safely get state coordinates
const getStateCoordinates = (stateValue: string) => {
  if (!stateValue)
    return {
      center: { lat: -23.55, lng: -46.64 }, // Default to São Paulo
      zoom: 8,
    };

  const normalizedState = stateValue.toLowerCase().trim();
  // Try direct match first
  if (STATE_COORDINATES[normalizedState]) {
    return STATE_COORDINATES[normalizedState];
  }

  // Try to match by partial key (in case the format is different)
  const stateKey = Object.keys(STATE_COORDINATES).find((key) =>
    key.includes(normalizedState.replace(/[()]/g, "").split(" ")[0]),
  );

  return stateKey
    ? STATE_COORDINATES[stateKey]
    : {
        center: { lat: -23.55, lng: -46.64 }, // Default to São Paulo
        zoom: 8,
      };
};

export const headers: HeadersFunction = reuseUsefulLoaderHeaders;

export async function loader({ request }: LoaderFunctionArgs) {
  const timings = makeTimings("stations loader"); // <-- 1. Setup Timings
  const url = new URL(request.url);

  const params = Object.fromEntries(
    Array.from(url.searchParams.entries(), ([key, value]) => [
      decodeURIComponent(key.toLowerCase()),
      decodeURIComponent(value.replace(/\+/g, " ")),
    ]),
  );

  const states = await prisma.state.findMany({
    select: {
      value: true,
      label: true,
    },
  });

  const slug = url.pathname.split("/").pop();
  const page = parseInt(params.page) || 1; // Default to page 1 if not provided
  const pageSize = 16; // Default page size to 16 if not provided.
  const limit = pageSize;
  const offset = (page - 1) * pageSize;
  let zoomLevel = 14;

  let chargingStations: any[] = [];
  let userLat: any = -23.533773;
  let userLon: any = -46.62529;
  let filteredLocations: any[] = [];
  let cars: any[] = [];
  let cities: any[] = [];
  let groupedData: any[] = [];

  if (params === undefined || Object.keys(params).length === 0) {
    if (slug === "estacoes") {
      filteredLocations = await time(
        () =>
          //@ts-ignore
          prisma.location.groupBy({
            by: ["city", "state"],
            _count: true,
            where: {
              country: "brasil",
            },
          }),
        { timings, type: "city state loader" },
      );
    }

    let grouped = filteredLocations.reduce((acc: any, cur: any) => {
      if (cur.state === null) {
        return acc;
      }
      if (!acc[cur.state]) {
        acc[cur.state] = [];
      }
      acc[cur.state].push(cur);
      return acc;
    }, {});

    for (const state in grouped) {
      grouped[state].sort((a: any, b: any) => a.city.localeCompare(b.city));
    }

    grouped = Object.fromEntries(
      Object.entries(grouped).sort((a: any, b: any) =>
        a[0].localeCompare(b[0]),
      ),
    );

    return json(
      {
        filteredLocations,
        chargingStations,
        params,
        userLat,
        userLon,
        page,
        cars,
        zoomLevel,
        states,
        cities,
        groupedData: grouped,
      },
      {
        headers: {
          "Cache-Control": "private, max-age=3600",
          "Server-Timing": timings.toString(),
        },
      },
    );
  }

  if (params.latitude && params.longitude) {
    userLat = params.latitude; // User's latitude
    userLon = params.longitude; // User's longitude

    const rows: any[] = await time(
      () => getRegionalStations(userLat, userLon, limit, offset),
      { timings, type: "regional loader" },
    );

    const locationsMap = new Map();

    for (const row of rows) {
      let location = locationsMap.get(row.locationId) as any;
      if (!location) {
        location = {
          id: row.locationId,
          access: row.access,
          address: row.address,
          city: row.city,
          state: row.state,
          country: row.country,
          type: row.type,
          icon: row.icon,
          icon_type: row.icon_type,
          latitude: row.latitude,
          longitude: row.longitude,
          name: row.name,
          score: row.score,
          stations: [],
          distance: row.distance,
          positives: row.positives,
          negatives: row.negatives,
          totalReviews: row.totalReviews,
          majorityNetworkId: row.majorityNetworkId,
          url: row.url,
        };
        locationsMap.set(row.locationId, location);
      }

      //@ts-ignore
      let station = location.stations.find((s) => s.id === row.stationId);
      if (!station) {
        station = {
          id: row.stationId,
          outlets: [],
        };
        location.stations.push(station);
      }

      station.outlets.push({
        id: row.id,
        connector: row.connector,
        kilowatts: row.kilowatts,
        power: row.power,
        status: row.status,
      });
    }

    chargingStations = Array.from(locationsMap.values());
    chargingStations.sort((a, b) => a.distance - b.distance);
  } else if (params.cidade && params.estado) {
    const stateData = await prisma.state.findUnique({
      where: {
        value: params.estado,
      },
      include: {
        cities: true,
      },
    });
    const cityData = stateData?.cities.find(
      (city) => city.value === params.cidade,
    );
    userLat = cityData?.latitude || null;
    userLon = cityData?.longitude || null;

    // If city coordinates are not found, fall back to state coordinates
    if (!userLat || !userLon) {
      const stateCoordinates = getStateCoordinates(params.estado);
      userLat = stateCoordinates.center.lat;
      userLon = stateCoordinates.center.lng;
      zoomLevel = stateCoordinates.zoom;
    }

    cities = await prisma.state.findMany({
      where: {
        value: params.estado,
      },
      select: {
        cities: true,
      },
    });

    cities = cities ? cities[0]?.["cities"] || [] : [];

    if (userLat && userLon) {
      // const rows: any[] = stmt.all(userLat, userLon, userLat, limit, offset))
      const rows: any[] = await time(
        () => getRegionalStations(userLat, userLon, limit, offset),
        { timings, type: "regional loader" },
      );

      const locationsMap = new Map();

      for (const row of rows) {
        let location = locationsMap.get(row.locationId) as any;
        if (!location) {
          location = {
            id: row.locationId,
            access: row.access,
            address: row.address,
            city: row.city,
            state: row.state,
            country: row.country,
            type: row.type,
            icon: row.icon,
            icon_type: row.icon_type,
            latitude: row.latitude,
            longitude: row.longitude,
            name: row.name,
            score: row.score,
            stations: [],
            distance: row.distance,
            positives: row.positives,
            negatives: row.negatives,
            totalReviews: row.totalReviews,
            majorityNetworkId: row.majorityNetworkId,
            url: row.url,
          };
          locationsMap.set(row.locationId, location);
        }

        //@ts-ignore
        let station = location.stations.find((s) => s.id === row.stationId);
        if (!station) {
          station = {
            id: row.stationId,
            outlets: [],
          };
          location.stations.push(station);
        }

        station.outlets.push({
          id: row.id,
          connector: row.connector,
          kilowatts: row.kilowatts,
          power: row.power,
          status: row.status,
        });
      }

      chargingStations = Array.from(locationsMap.values());
      chargingStations.sort((a, b) => a.distance - b.distance);
    }
  } else if (params.estado && !params.cidade) {
    const selectedState = params.estado as string;
    cities = await prisma.state.findMany({
      where: {
        value: params.estado,
      },
      select: {
        cities: true,
      },
    });

    cities = cities ? cities[0]?.["cities"] || [] : [];

    const stateCoordinates = getStateCoordinates(selectedState);
    userLat = stateCoordinates.center.lat;
    userLon = stateCoordinates.center.lng;
    zoomLevel = stateCoordinates.zoom;

    if (userLat && userLon) {
      // const rows: any[] = stmt.all(userLat, userLon, userLat, limit, offset))
      const rows: any[] = await time(
        () => getRegionalStations(userLat, userLon, limit, offset),
        { timings, type: "regional loader" },
      );

      const locationsMap = new Map();

      for (const row of rows) {
        let location = locationsMap.get(row.locationId) as any;
        if (!location) {
          location = {
            id: row.locationId,
            access: row.access,
            address: row.address,
            city: row.city,
            state: row.state,
            country: row.country,
            type: row.type,
            icon: row.icon,
            icon_type: row.icon_type,
            latitude: row.latitude,
            longitude: row.longitude,
            name: row.name,
            score: row.score,
            stations: [],
            distance: row.distance,
            positives: row.positives,
            negatives: row.negatives,
            totalReviews: row.totalReviews,
            majorityNetworkId: row.majorityNetworkId,
            url: row.url,
          };
          locationsMap.set(row.locationId, location);
        }

        //@ts-ignore
        let station = location.stations.find((s) => s.id === row.stationId);
        if (!station) {
          station = {
            id: row.stationId,
            outlets: [],
          };
          location.stations.push(station);
        }

        station.outlets.push({
          id: row.id,
          connector: row.connector,
          kilowatts: row.kilowatts,
          power: row.power,
          status: row.status,
        });
      }

      chargingStations = Array.from(locationsMap.values());
      chargingStations.sort((a, b) => a.distance - b.distance);
    }
  } else {
    chargingStations = [];
  }

  if (params.cidade && params.estado) {
    cars = await time(
      () =>
        prisma.car.findMany({
          select: {
            id: true,
            maker: true,
            model: true,
            updatedAt: true,
            images: {
              select: {
                id: true,
              },
              where: {
                order: 0,
              },
            },
          },
          where: {
            city: params.cidade,
            state: params.estado,
            isInactive: false,
            isRemoved: false,
          },
          orderBy: {
            updatedAt: "desc",
          },
          take: 3,
          distinct: ["maker", "model"],
        }),
      { timings, type: "car loader" },
    );
  }

  //loop through latitudes and longitudes, add salt and convert to base64. The salt is 'bkWUdYqNoedWDz9RZhZL'
  for (let i = 0; i < chargingStations.length; i++) {
    chargingStations[i].latitude = Buffer.from(
      (chargingStations[i].latitude + "bkWUdYqNoedWDz9RZhZL").toString(),
    ).toString("base64");
    chargingStations[i].longitude = Buffer.from(
      (chargingStations[i].longitude + "bkWUdYqNoedWDz9RZhZL").toString(),
    ).toString("base64");
  }

  //loop through latitudes and longitudes, add salt and convert to base64. The salt is 'bkWUdYqNoedWDz9RZhZL'
  userLat = Buffer.from((userLat + "bkWUdYqNoedWDz9RZhZL").toString()).toString(
    "base64",
  );
  userLon = Buffer.from((userLon + "bkWUdYqNoedWDz9RZhZL").toString()).toString(
    "base64",
  );

  return json(
    {
      chargingStations,
      params,
      userLat,
      userLon,
      filteredLocations,
      cars,
      zoomLevel,
      states,
      cities,
      groupedData,
    },
    {
      headers: {
        "Cache-Control": "private, max-age=3600",
        "Server-Timing": timings.toString(),
      },
    },
  );
}

export const meta: MetaFunction = ({ location }) => {
  const searchQuery = new URLSearchParams(location.search);

  let state = searchQuery.get("estado");
  let city = searchQuery.get("cidade");

  //clean maker and capitalize first letters
  if (city) {
    city = city.replace(/-/g, " ").replace(/\w\S*/g, function (txt) {
      return txt.charAt(0).toUpperCase() + txt.substring(1).toLowerCase();
    });
  }

  //clean state and capitalize first letters
  if (state) {
    state = state.replace(/-/g, " ").replace(/\w\S*/g, function (txt) {
      return txt.charAt(0).toUpperCase() + txt.substring(1).toLowerCase();
    });
  }

  //build url
  const currentUrl = `https://carregados.com.br/estacoes${location.search}`;

  let title = "Estações de Carregamento de Carros Elétricos";

  //build title conditionally with maker and state
  if (city && state) {
    title = `Estações de Carregamento em ${city} - ${formatState(state)}`;
  } else if (state) {
    title = `Estações de Carregamento em ${formatState(state)}`;
  } else if (city) {
    title = `Estações de Carregamento em ${city}`;
  }

  //build description conditionally with maker and state
  let description =
    "Veja as estações de carregamento para carros elétricos perto de você.";
  if (state && city) {
    description = `Veja as estações de carregamento para carros elétricos em ${city} - ${formatState(
      state,
    )}.`;
  } else if (state) {
    description = `Veja as estações de carregamento para carros elétricos em ${formatState(
      state,
    )}.`;
  } else if (city) {
    description = `Veja as estações de carregamento para carros elétricos em ${city}.`;
  }

  return [
    { title: title },
    {
      name: "description",
      content: description,
    },
    {
      itemProp: "name",
      content: title,
    },
    {
      itemProp: "description",
      content: description,
    },
    { itemProp: "image", content: "https://carregados.com.br/img/og-main.jpg" },
    { property: "og:url", content: currentUrl },
    { property: "og:type", content: "website" },
    {
      property: "og:title",
      content: title,
    },
    {
      property: "og:description",
      content: description,
    },
    {
      property: "og:image",
      content: "https://carregados.com.br/img/og-main.jpg",
    },
    { name: "twitter:card", content: "summary_large_image" },
    {
      name: "twitter:title",
      content: title,
    },
    {
      name: "twitter:description",
      content: description,
    },
    {
      name: "twitter:image",
      content: "https://carregados.com.br/img/og-main.jpg",
    },
    {
      name: "twitter:image:alt",
      content: title,
    },
    {
      tagName: "link",
      rel: "canonical",
      href: currentUrl,
    },
  ];
};

// Debounce function to optimize event handling
function debounce<T extends (...args: any[]) => void>(
  func: T,
  wait: number,
): (...args: Parameters<T>) => void {
  let timeout: ReturnType<typeof setTimeout> | null = null;
  return function executedFunction(...args: Parameters<T>) {
    const later = () => {
      if (timeout) clearTimeout(timeout);
      func(...args);
    };
    if (timeout) clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
}

export default function Estacoes() {
  const fetcher = useFetcher<any>();
  const pageFetcher = useFetcher<any>();
  const [searchParams, setSearchParams] = useSearchParams();
  const {
    chargingStations,
    params,
    filteredLocations,
    cars,
    userLat,
    userLon,
    zoomLevel,
    states,
    cities,
    groupedData,
  } = useLoaderData<typeof loader>();
  const curFilteredLocations = filteredLocations;
  const [curChargingStations, setCurChargingStations] =
    useState<any[]>(chargingStations);
  const [location, setLocation] = useState<any>({
    latitude: params.userLat || undefined,
    longitude: params.userLon || undefined,
    cidade: params.cidade || undefined,
    estado: params.estado || undefined,
  });

  const user = useOptionalUser();
  const favorites = useMemo(
    () => user?.favoriteStations?.map((favorite) => favorite.locationId) || [],
    [user?.favoriteStations],
  );

  const [scrollPosition, setScrollPosition] = useState(0);
  const [clientHeight, setClientHeight] = useState(0);
  const heightRef = useRef<HTMLDivElement | null>(null);
  const [shouldFetch, setShouldFetch] = useState(true);
  const [page, setPage] = useState(2);
  const [isLoading, setIsLoading] = useState(false);

  // Set the height of the parent container whenever photos are loaded
  const divHeight = useCallback((node: HTMLDivElement | null) => {
    if (node !== null) {
      heightRef.current = node;
    }
  }, []);

  // Function to load next page - extracted to reduce duplicate code
  const loadNextPage = useCallback(() => {
    if (!shouldFetch || isLoading) return;

    const paramsWithPage = searchParams.toString();
    if (paramsWithPage) {
      setIsLoading(true);
      pageFetcher.load(`/estacoes?${paramsWithPage}&page=${page}`);
    }

    setShouldFetch(false);
  }, [searchParams, page, shouldFetch, isLoading, pageFetcher]);

  // Optimized scroll listener with debounce
  useEffect(() => {
    const getHeight = () =>
      heightRef.current?.getBoundingClientRect().height || 0;

    const scrollListener = debounce(() => {
      if (!shouldFetch || isLoading) return;

      // Calculate these values inside the event handler to get fresh values
      const currentClientHeight = window.innerHeight;
      const currentScrollPosition = window.scrollY;
      const currentHeight = getHeight();

      setClientHeight(currentClientHeight);
      setScrollPosition(currentScrollPosition);

      if (currentClientHeight + currentScrollPosition + 500 >= currentHeight) {
        loadNextPage();
      }
    }, 100); // Debounce by 100ms

    // Initial calculation
    setClientHeight(window.innerHeight);

    // Avoid running during SSR
    if (typeof window !== "undefined") {
      window.addEventListener("scroll", scrollListener, { passive: true });
    }

    // Clean up
    return () => {
      if (typeof window !== "undefined") {
        window.removeEventListener("scroll", scrollListener);
      }
    };
  }, [shouldFetch, isLoading, loadNextPage]);

  // Merge charging stations, increment page, and allow fetching again
  useEffect(() => {
    if (pageFetcher.state === "idle" && pageFetcher.data) {
      const newStations = pageFetcher.data.chargingStations;

      if (!newStations || newStations.length === 0) {
        setShouldFetch(false);
      } else {
        setCurChargingStations((prevStations) => {
          // Create a Set of existing station IDs for efficient lookup
          const existingIds = new Set(
            prevStations.map((s: { id: any }) => s.id),
          );
          // Filter out new stations that already exist in the previous state
          const uniqueNewStations = newStations.filter(
            (s: { id: any }) => !existingIds.has(s.id),
          );
          // Append only the unique new stations
          return [...prevStations, ...uniqueNewStations];
        });
        setPage((prevPage) => prevPage + 1);
        setShouldFetch(true);
      }

      setIsLoading(false);
    }
  }, [pageFetcher.data, pageFetcher.state]);

  const handleLocation = useCallback(() => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const { latitude, longitude } = position.coords;

          setLocation({
            latitude,
            longitude,
            cidade: undefined,
            estado: undefined,
          });

          const params = new URLSearchParams();
          params.set("latitude", latitude.toString());
          params.set("longitude", longitude.toString());
          setSearchParams(params);
        },
        (error) => {
          console.error("Error occurred while getting location", error);
        },
        {
          maximumAge: 60000, // Cache position for 1 minute
          timeout: 10000, // 10-second timeout
        },
      );
    } else {
      console.error("Geolocation is not supported by this browser.");
    }
  }, [setSearchParams]);

  // Load data when search params change
  useEffect(() => {
    setShouldFetch(true);
    setPage(2);
    fetcher.load(`/estacoes?${searchParams.toString()}`);
  }, [searchParams]);

  // Update stations when data is loaded
  useEffect(() => {
    if (fetcher.data) {
      const data = fetcher.data as any;
      setCurChargingStations(data.chargingStations);
    }
  }, [fetcher.data]);

  const { stationId } = useParams();

  // Memoize the page title and description for better performance
  const pageInfo = useMemo(() => {
    let title = "";
    let description = "";

    if (location.cidade && location.estado) {
      title = `Estações de carregamento em ${location.cidade} - ${formatState(location.estado)}`;
      description =
        "Veja as estações de carregamento disponíveis para carros elétricos e híbridos em sua cidade.";
    } else if (location.estado) {
      title = `Estações de carregamento em ${formatState(location.estado)}`;
      description =
        "Veja as estações de carregamento disponíveis para carros elétricos e híbridos em seu estado.";
    } else if (location.latitude && location.longitude) {
      title = "Estações de carregamento próximas de você";
      description =
        "Veja as estações de carregamento disponíveis para carros elétricos e híbridos próximas de você.";
    } else {
      return null;
    }

    return { title, description };
  }, [location]);

  if (stationId && curChargingStations.length === 0) {
    return (
      <div className="md:bg-gray-50 md:py-10 md:dark:bg-background">
        <Outlet />
      </div>
    );
  }

  if (curFilteredLocations.length > 0) {
    return (
      <CitySuggestions
        filteredLocations={filteredLocations}
        handleLocation={handleLocation}
        location={location}
        setLocation={setLocation}
        setSearchParams={setSearchParams}
        states={states}
        cities={cities}
        groupedData={groupedData}
      />
    );
  }

  return (
    <main className="bg-gray-50 dark:bg-background">
      <Outlet />
      <section className="mx-auto max-w-6xl p-4 py-10 md:py-16">
        <div className="flex w-full items-center space-x-2">
          <div className="flex w-full flex-col items-center justify-center space-y-4 md:w-2/3 md:flex-row md:space-x-2 md:space-y-0">
            <div id="state" className="flex w-full flex-col space-y-2">
              <Combobox
                labelProps={{ children: "Estado" }}
                inputProps={{ "aria-label": "Estado" }}
                inputName="estado"
                defaultData={location.estado || ""}
                handleStateChange={(value, inputName) => {
                  const params = new URLSearchParams();
                  params.set("estado", value);
                  setSearchParams(params);
                  setLocation({
                    ...location,
                    estado: value,
                    cidade: undefined,
                    latitude: undefined,
                    longitude: undefined,
                  });
                }}
                showErrors={false}
                items={states}
              />
            </div>
            <div id="city" className="flex w-full flex-col space-y-2">
              <Combobox
                labelProps={{ children: "Cidade" }}
                inputProps={{ "aria-label": "Cidade" }}
                inputName="cidade"
                defaultData={location.cidade || ""}
                handleStateChange={(value, inputName) => {
                  setSearchParams((prev) => {
                    prev.set("cidade", value);
                    prev.delete("latitude");
                    prev.delete("longitude");
                    return prev;
                  });
                  setLocation({
                    ...location,
                    cidade: value,
                    latitude: undefined,
                    longitude: undefined,
                  });
                }}
                showErrors={false}
                items={location.estado ? cities : []}
              />
            </div>
            <div className="flex w-full">
              <Button
                className="animate-gradient-x flex w-full items-center justify-center gap-1 border-0 bg-gradient-to-r from-purple-600 via-pink-500 to-yellow-500 text-white hover:text-gray-200 hover:opacity-80 md:mt-6"
                onClick={() => handleLocation()}
                variant={"outline"}
                aria-label="Localizar estação de carregamento próximas"
              >
                <Locate /> Estações próximas de mim
              </Button>
            </div>
          </div>
        </div>
        <div className="mb-2 flex flex-col gap-4 pb-4 pt-8 text-sm">
          <ClientOnly
            fallback={<Skeleton className="h-[480px] w-full rounded-lg" />}
          >
            {() => (
              <div id="container" className="h-[480px] w-full">
                <Mapa
                  chargingStations={curChargingStations}
                  favorites={favorites}
                  userLat={userLat}
                  userLon={userLon}
                  zoomLevel={zoomLevel - 2}
                />
              </div>
            )}
          </ClientOnly>
          <div className="hidden w-full items-center gap-4 pb-4 font-semibold md:flex">
            <div className="flex items-center justify-center gap-1">
              <div className="h-4 w-14 rounded-lg border bg-blue-500" />
              Carregamento Rápido
            </div>
            <div className="flex items-center justify-center gap-1">
              <div className="h-4 w-14 rounded-lg border bg-green-500" />
              Público
            </div>
            <div className="flex items-center justify-center gap-1">
              <div className="h-4 w-14 rounded-lg border bg-yellow-500" />
              Uso Restrito
            </div>
            <div className="flex items-center justify-center gap-1">
              <div className="h-4 w-14 rounded-lg border bg-red-500" />
              Em Manutenção
            </div>
            <div className="flex items-center justify-center gap-1">
              <div className="h-4 w-14 rounded-lg border bg-purple-500" />
              Favoritas
            </div>
          </div>
          {cars.length > 0 && location.cidade && location.estado && (
            <div className="flex w-full pb-6">
              <CarPreview
                carsInLocation={cars}
                city={location.cidade}
                state={location.estado}
              />
            </div>
          )}
          {pageInfo && (
            <>
              <h1 className="text-xl font-bold capitalize">{pageInfo.title}</h1>
              <div className="flex gap-1 text-muted-foreground">
                {pageInfo.description}
              </div>
            </>
          )}
        </div>
        <div ref={divHeight} className="min-h-screen space-y-4">
          {curChargingStations.length > 0 ? (
            curChargingStations.map((station: any, index: any) => (
              <Link
                prefetch="intent"
                preventScrollReset
                state={{ history: `/estacoes?${searchParams.toString()}` }}
                to={`/estacoes/${station.url}${
                  station.id
                }?${searchParams.toString()}`}
                key={station.id || index}
              >
                <Station station={station} />
              </Link>
            ))
          ) : (location.cidade && location.estado) ||
            (location.latitude && location.longitude) ? (
            <div className="mt-16 flex w-full flex-col items-center justify-center space-y-4 py-20">
              <span className="text-xl font-semibold text-primary">
                Nenhuma estação encontrada em um raio de 75km :(
              </span>
            </div>
          ) : (
            <div className="mt-16 flex w-full flex-col items-center justify-center space-y-4 py-20">
              <span className="text-xl font-semibold text-primary">
                Selecione uma cidade acima ou use sua localização para ver as
                estações de carregamento mais próximas de você.
              </span>
            </div>
          )}
          {(pageFetcher.state !== "idle" || isLoading) && (
            <div className="mt-6 flex w-full items-center justify-center space-x-2 text-white">
              <svg
                className="h-5 w-5 animate-spin"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx={12}
                  cy={12}
                  r={10}
                  stroke="currentColor"
                  strokeWidth={4}
                />
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 0 1 8-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 0 1 4 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                />
              </svg>
              <span className="font-semibold">Carregando...</span>
            </div>
          )}
        </div>
      </section>
    </main>
  );
}

export const handle: SEOHandle = {
  //@ts-ignore
  getSitemapEntries: serverOnly$(async (request) => {
    let locations = await prisma.location.findMany({
      select: {
        city: true,
        state: true,
        updatedAt: true,
      },
      where: {
        country: "brasil",
      },
      orderBy: {
        updatedAt: "desc",
      },
      distinct: ["city", "state"],
    });

    locations = locations.filter((location) => location.city && location.state);

    const lastLocationAdded = await prisma.location.findFirst({
      orderBy: {
        updatedAt: "desc",
      },
      where: {
        country: "brasil",
      },
      select: {
        updatedAt: true,
      },
    });

    return [
      ...locations.map((location) => {
        //@ts-ignore
        return {
          route: `/estacoes?cidade=${encodeURIComponent(
            location.city!,
          )}&amp;estado=${encodeURIComponent(location.state!)}`,
          priority: 0.7,
          changefreq: "monthly",
          lastmod: location.updatedAt.toISOString(),
        };
      }),
      {
        route: "/estacoes",
        priority: 0.7,
        changefreq: "monthly",
        lastmod: lastLocationAdded!.updatedAt.toISOString(),
      },
    ];
  }),
};

export function ErrorBoundary() {
  return <GeneralErrorBoundary />;
}
